// apps/web/src/components/coletor/fila-local.tsx — Fase 02, Plano 08 (APT-03, D-11).
import { useState, useEffect, type ReactElement } from 'react';
import {
  listarOutbox,
  obterEstatisticasOutbox,
  type OutboxEventRecord,
} from '../../lib/offline/outbox';

export function FilaLocalBadge(): ReactElement {
  const [stats, setStats] = useState<{ pendentes: number; sincronizados: number; conflitos: number }>({
    pendentes: 0,
    sincronizados: 0,
    conflitos: 0,
  });
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [events, setEvents] = useState<readonly OutboxEventRecord[]>([]);
  const [isOnline, setIsOnline] = useState<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);

  const refreshData = async () => {
    const s = await obterEstatisticasOutbox();
    setStats(s);
    if (drawerOpen) {
      const list = await listarOutbox();
      setEvents(list);
    }
  };

  useEffect(() => {
    void refreshData();

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const interval = setInterval(() => {
      void refreshData();
    }, 5000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [drawerOpen]);

  return (
    <>
      {/* Badge Fixo no Cabeçalho */}
      <div className="flex items-center gap-2">
        {!isOnline && (
          <span className="px-2.5 py-1 bg-amber-900/80 border border-amber-500/50 text-amber-200 text-xs font-bold rounded-lg flex items-center gap-1.5 shadow">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
            Offline (Salvo local)
          </span>
        )}

        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="px-3 py-1 bg-slate-800 hover:bg-slate-700 active:bg-slate-900 border border-slate-700 text-slate-200 text-xs font-mono rounded-lg flex items-center gap-2 shadow"
        >
          <span>{stats.pendentes === 0 && stats.conflitos === 0 ? 'Tudo sincronizado' : 'Fila de sincronização:'}</span>
          {stats.conflitos > 0 && (
            <span className="px-1.5 py-0.5 bg-red-600 text-white rounded font-bold text-[10px]">
              {stats.conflitos} conflito{stats.conflitos > 1 ? 's' : ''}
            </span>
          )}
          {stats.pendentes > 0 && (
            <span className="px-1.5 py-0.5 bg-amber-500 text-amber-950 rounded font-bold text-[10px]">
              {stats.pendentes} pendente{stats.pendentes > 1 ? 's' : ''}
            </span>
          )}
          {stats.sincronizados > 0 && stats.pendentes === 0 && stats.conflitos === 0 && (
            <span className="px-1.5 py-0.5 bg-emerald-600 text-white rounded font-bold text-[10px]">
              {stats.sincronizados} sync
            </span>
          )}
        </button>
      </div>

      {/* Drawer / Modal com a Fila de Eventos */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/70 backdrop-blur-sm">
          <div className="w-full max-w-md bg-slate-900 border-l border-slate-800 h-full p-6 flex flex-col justify-between overflow-y-auto text-slate-100 shadow-2xl animate-fade-in">
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                <h3 className="text-base font-bold text-white uppercase tracking-wider">
                  Fila Local do Coletor
                </h3>
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  className="text-slate-400 hover:text-white font-bold text-lg"
                >
                  ✕
                </button>
              </div>

              {!isOnline && (
                <div className="p-3 bg-amber-950/80 border border-amber-500/40 text-amber-200 rounded-xl text-xs">
                  <span className="font-bold">Sem conexão.</span> Salvo localmente na memória do tablet. Será
                  enviado automaticamente ao restabelecer a rede.
                </div>
              )}

              {events.length === 0 ? (
                <div className="py-12 text-center text-xs text-slate-400">
                  Nenhum evento pendente. Tudo sincronizado.
                </div>
              ) : (
                <div className="space-y-3">
                  {events.map((evt) => (
                    <div
                      key={evt.id}
                      className={`p-4 rounded-xl border text-xs space-y-2 ${
                        evt.status === 'conflito'
                          ? 'bg-red-950/60 border-red-500/60 text-red-200'
                          : evt.status === 'pendente'
                            ? 'bg-amber-950/40 border-amber-500/40 text-amber-200'
                            : 'bg-slate-950/40 border-slate-800 text-slate-300'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-mono font-bold text-white">
                          OS #{evt.aggregateId.slice(0, 8)}...
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                            evt.status === 'conflito'
                              ? 'bg-red-600 text-white'
                              : evt.status === 'pendente'
                                ? 'bg-amber-500 text-amber-950'
                                : 'bg-emerald-600 text-white'
                          }`}
                        >
                          {evt.status}
                        </span>
                      </div>

                      <div className="text-[11px] text-slate-400 flex justify-between">
                        <span>Horário:</span>
                        <span>{new Date(evt.createdAt).toLocaleTimeString()}</span>
                      </div>

                      {/* Conflito de OS Concorrente (D-11) */}
                      {evt.status === 'conflito' && (
                        <div className="pt-2 border-t border-red-800/60 space-y-2">
                          <p className="text-[11px] font-medium text-red-300">
                            {evt.conflictCode
                              ? `O servidor recusou este apontamento (${evt.conflictCode}). Precisa da revisão de um supervisor.`
                              : 'Este evento precisa da revisão de um supervisor (conflito de concorrência).'}
                          </p>
                          <button
                            type="button"
                            onClick={() => alert('Supervisor acionado para verificação técnica desta OS.')}
                            className="w-full py-2 bg-red-600 hover:bg-red-500 text-white font-bold rounded-lg text-xs"
                          >
                            Chamar Supervisor
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="w-full min-h-[48px] py-3 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs rounded-xl mt-6"
            >
              Fechar Fila
            </button>
          </div>
        </div>
      )}
    </>
  );
}
